import React, { useState, useEffect, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import "../styles/SpeakerForm.css";
import { API_BASE_URL } from "../config";

/* ------------------------------------------------------------------ */
/* FILE UPLOAD COMPONENT                                               */
/* variant="avatar" -> round profile-photo uploader (new design)       */
/* ------------------------------------------------------------------ */
const formatFileSize = (bytes) => {
  if (bytes < 1024) return bytes + " bytes";
  else if (bytes < 1048576) return (bytes / 1024).toFixed(1) + " KB";
  return (bytes / 1048576).toFixed(1) + " MB";
};

const getFileIcon = (filename = "") => {
  const ext = filename.split(".").pop().toLowerCase();
  if (ext === "pdf") return "📄";
  if (ext === "ppt" || ext === "pptx") return "📊";
  if (ext === "zip") return "📦";
  return "📎";
};

const FileUpload = ({
  accept,
  maxSize,
  allowedTypes,
  onFileSelect,
  existingFile,
  isImage = false,
  variant = "default",
}) => {
  const [preview, setPreview] = useState(
    existingFile
      ? {
          url: existingFile.data || existingFile.url,
          name: existingFile.name,
          size: existingFile.size,
        }
      : null,
  );
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef(null);

  const handleFiles = (files) => {
    const file = files[0];
    if (!file) return;

    if (file.size > maxSize) {
      alert(
        `File ${file.name} is too large. Maximum size is ${formatFileSize(maxSize)}.`,
      );
      return;
    }
    if (isImage && !allowedTypes.includes(file.type)) {
      alert(`File ${file.name} must be an image (JPEG, PNG)`);
      return;
    }

    if (isImage && file.type.startsWith("image/")) {
      const reader = new FileReader();
      reader.onload = (e) => {
        setPreview({ url: e.target.result, name: file.name, size: file.size });
        onFileSelect({
          name: file.name,
          size: file.size,
          type: file.type,
          data: e.target.result,
        });
      };
      reader.readAsDataURL(file);
    } else {
      setPreview({ name: file.name, size: file.size, type: file.type });
      onFileSelect({ name: file.name, size: file.size, type: file.type });
    }
  };

  const removeFile = (e) => {
    e.stopPropagation();
    setPreview(null);
    onFileSelect(null);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const dragProps = {
    onClick: () => fileInputRef.current?.click(),
    onDragOver: (e) => {
      e.preventDefault();
      setIsDragging(true);
    },
    onDragLeave: () => setIsDragging(false),
    onDrop: (e) => {
      e.preventDefault();
      setIsDragging(false);
      if (e.dataTransfer.files.length) handleFiles(e.dataTransfer.files);
    },
  };

  const hiddenInput = (
    <input
      ref={fileInputRef}
      type="file"
      accept={accept}
      style={{ display: "none" }}
      onChange={(e) => e.target.files.length && handleFiles(e.target.files)}
    />
  );

  /* ---------- Avatar variant (profile photo) ---------- */
  if (variant === "avatar") {
    return (
      <div
        className={`photo-dropzone ${isDragging ? "dragover" : ""}`}
        {...dragProps}
      >
        <div className="photo-avatar">
          {preview?.url ? (
            <img src={preview.url} alt="Profile preview" />
          ) : (
            <i className="fas fa-user"></i>
          )}
          <span className="photo-avatar-badge">
            <i className="fas fa-camera"></i>
          </span>
        </div>

        <div className="photo-dropzone-title">
          {preview ? preview.name : "Click to upload"}
        </div>
        <div className="photo-dropzone-sub">
          {preview ? formatFileSize(preview.size) : "or drag and drop"}
        </div>
        {!preview && (
          <div className="photo-dropzone-hint">PNG, JPG up to 5MB</div>
        )}

        {preview && (
          <button type="button" className="photo-remove" onClick={removeFile}>
            <i className="fas fa-times"></i> Remove
          </button>
        )}
        {hiddenInput}
      </div>
    );
  }

  /* ---------- Default variant (custom form FileUpload field) ---------- */
  return (
    <>
      <div
        className={`file-upload-container ${isDragging ? "dragover" : ""}`}
        {...dragProps}
      >
        <div className="file-upload-icon">
          <i className={isImage ? "fas fa-camera" : "fas fa-file-upload"}></i>
        </div>
        <div className="file-upload-text">
          <strong>Click to upload</strong> or drag and drop
        </div>
        <div className="file-upload-subtext">
          {isImage ? "PNG, JPG up to 5MB" : "PDF, PPT, ZIP up to 20MB"}
        </div>
        {hiddenInput}
      </div>

      {preview && (
        <div className="file-preview">
          <div className="preview-item">
            {isImage && preview.url ? (
              <img src={preview.url} alt="Preview" />
            ) : (
              <div className="preview-file">
                <div className="preview-file-icon">
                  {getFileIcon(preview.name)}
                </div>
                <div className="preview-file-name">{preview.name}</div>
                <div className="preview-file-size">
                  {formatFileSize(preview.size)}
                </div>
              </div>
            )}
            <button
              type="button"
              className="preview-remove"
              onClick={removeFile}
            >
              <i className="fas fa-times"></i>
            </button>
          </div>
        </div>
      )}
    </>
  );
};

const DEFAULT_SPEAKER_FIELDS = [
  {
    label: "Full Name",
    field_type: "TextInput",
    is_required: true,
    place_holder: "Enter your full name",
    options: [],
  },
  {
    label: "Email Address",
    field_type: "EmailInput",
    is_required: true,
    place_holder: "Enter your email address",
    options: [],
  },
  {
    label: "Talk Topic",
    field_type: "TextInput",
    is_required: true,
    place_holder: "Enter your talk topic",
    options: [],
  },
  {
    label: "Talk Details",
    field_type: "TextArea",
    is_required: true,
    place_holder: "Describe your talk",
    options: [],
  },
];


const isSpeakerRegistrationOpen = (startDate, endDate) => {
  const now = new Date();

  if (startDate) {
    const start = new Date(startDate);

    if (!Number.isNaN(start.getTime()) && now < start) {
      return false;
    }
  }

  if (endDate) {
    const end = new Date(endDate);

    // Include the complete end date when API returns YYYY-MM-DD
    if (/^\d{4}-\d{2}-\d{2}$/.test(endDate)) {
      end.setHours(23, 59, 59, 999);
    }

    if (!Number.isNaN(end.getTime()) && now > end) {
      return false;
    }
  }

  return true;
};
const buildInitialValues = (fields) => {
  const init = {};
  fields.forEach((f) => {
    init[f.label] = f.field_type === "Checkbox" ? false : "";
  });
  return init;
};

/* Small helper: input with a leading icon */
const IconInput = ({ icon, children, className = "" }) => (
  <div className={`input-icon-wrap ${className}`}>
    <i className={`${icon} input-icon`}></i>
    {children}
  </div>
);

/* ------------------------------------------------------------------ */
/* SPEAKER FORM COMPONENT                                              */
/* ------------------------------------------------------------------ */
const SpeakerForm = () => {
  const { id: eventId } = useParams();
  const navigate = useNavigate();

  const [currentStep, setCurrentStep] = useState(1);
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [submissionId, setSubmissionId] = useState("");
  const [submissionDate, setSubmissionDate] = useState("");
  const [errors, setErrors] = useState({});
  const [toast, setToast] = useState("");

  const [eventData, setEventData] = useState(null);
  const [eventLoading, setEventLoading] = useState(true);
  const [eventError, setEventError] = useState(null);

  const [speakerFormFields, setSpeakerFormFields] = useState([]);
  const [speakerFormValues, setSpeakerFormValues] = useState({});
  const [citiesList, setCitiesList] = useState([]);

  const [formData, setFormData] = useState({
    // Step 1
    firstName: "",
    lastName: "",
    email: "",
    countryCode: "+91",
    phone: "",
    city: "",
    jobTitle: "",
    company: "",
    bio: "",
    photo: null,

    // Step 3
    experience: "",
    previousTalks: "",
    twitter: "",
    linkedin: "",
    github: "",
    links: "",
    materials: [],
    specialRequirements: "",
    travelSupport: false,
    travelDetails: "",

    // Step 4
    agreement1: false,
    agreement2: false,
    agreement3: false,
    newsletter: false,
  });

  /* ---------- Fetch event details ---------- */
  useEffect(() => {
    const token = localStorage.getItem("token");
    setEventLoading(true);
    fetch(`${API_BASE_URL}/event/${eventId}`, {
      method: "GET",
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      },
    })
      .then((res) => {
        if (!res.ok) throw new Error("Event not found");
        return res.json();
      })
      .then((data) => {
        const meta = data?.metadata;
        console.log("Event metadata:", meta);
console.log("Venue:", meta?.venue);
console.log("City:", meta?.city);
console.log("Speaker deadline:", meta?.speaker_configuration?.end_date);
        const cfg = meta?.speaker_configuration;
        setEventData({
          id: meta?.event_id,
          image: data?.logo_file?.base64,
          banner: data?.banner_file?.base64,
          name: meta?.name,
          description: meta?.description,
          venue: meta?.venue,
          city: meta?.city,
          start_date: meta?.start_date,
          end_date: meta?.end_date,
          attendee_limit: meta?.attendees_configuration?.default_attendee_limit,
          speaker_limit: cfg?.speaker_limit,
          speaker_start_date: cfg?.start_date,
          speaker_end_date: cfg?.end_date,
        });

        const formType = cfg?.speaker_form_type || "default";
        const customFields = cfg?.custom_form;
        const fields =
          formType === "custom" &&
          Array.isArray(customFields) &&
          customFields.length > 0
            ? customFields
            : DEFAULT_SPEAKER_FIELDS;

        setSpeakerFormFields(fields);
        setSpeakerFormValues(buildInitialValues(fields));
        setEventError(null);
      })
      .catch(() => {
        setEventError("Failed to load event details");
        setSpeakerFormFields(DEFAULT_SPEAKER_FIELDS);
        setSpeakerFormValues(buildInitialValues(DEFAULT_SPEAKER_FIELDS));
      })
      .finally(() => setEventLoading(false));
  }, [eventId]);

  /* ---------- Cities ---------- */
  useEffect(() => {
    fetch(`${API_BASE_URL}/getCities`)
      .then((res) => res.json())
      .then((data) => {
        if (data?.status === "success" && Array.isArray(data.data)) {
          const cities = data.data
            .map((c) => ({ id: c.id, city_name: c.city_name }))
            .sort((a, b) => a.city_name.localeCompare(b.city_name));
          setCitiesList(cities);
        }
      })
      .catch((err) => console.error("❌ [Cities] Failed to load cities:", err));
  }, []);

  /* ---------- Draft restore / autosave ---------- */
  useEffect(() => {
    const savedData = localStorage.getItem("speakerApplication");
    if (savedData) {
      try {
        setFormData((prev) => ({ ...prev, ...JSON.parse(savedData) }));
      } catch (e) {
        console.log("⚠️ [Storage] No valid saved data found");
      }
    }
  }, []);

  useEffect(() => {
    if (!isSubmitted) {
      localStorage.setItem("speakerApplication", JSON.stringify(formData));
    }
  }, [formData, isSubmitted]);

  const showToast = (msg) => {
    setToast(msg);
    setTimeout(() => setToast(""), 2500);
  };

  const handleSaveDraft = () => {
    localStorage.setItem("speakerApplication", JSON.stringify(formData));
    showToast("Draft saved successfully");
  };

  const updateFormData = (field, value) =>
    setFormData((prev) => ({ ...prev, [field]: value }));

  const updateSpeakerFormValue = (label, value) =>
    setSpeakerFormValues((prev) => ({ ...prev, [label]: value }));

  const goToStep = (n) => {
    setCurrentStep(n);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  /* ---------- Validation ---------- */
  const validateEmail = (email) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

  const validatePhone = (phone) => {
    const digits = phone.replace(/\D/g, "");
    return digits.length >= 7 && digits.length <= 10;
  };

  const getWordCount = (text) =>
    text ? text.trim().split(/\s+/).filter(Boolean).length : 0;

  const validateStep1 = () => {
    const e = {};
    if (!formData.firstName.trim()) e.firstName = "Please enter your first name";
    if (!formData.lastName.trim()) e.lastName = "Please enter your last name";
    if (!formData.email.trim()) {
      e.email = "Please enter a valid professional email";
    } else if (!validateEmail(formData.email)) {
      e.email = "Please enter a valid email address";
    }
    if (!formData.phone.trim()) {
      e.phone = "Please enter a valid phone number";
    } else if (!validatePhone(formData.phone)) {
      e.phone = "Please enter a valid phone number (7–10 digits)";
    }
    if (!formData.city) e.city = "Please select your city";
    if (!formData.jobTitle.trim())
      e.jobTitle = "Please enter your current position";
    if (!formData.company.trim())
      e.company = "Please enter your company/organization";
    if (!formData.bio.trim()) {
      e.bio = "Please provide a professional biography (5-300 words)";
    } else {
      const wc = getWordCount(formData.bio);
      if (wc < 5 || wc > 300) e.bio = "Biography must be between 5-300 words";
    }
    if (!formData.photo) e.photo = "Please upload a profile photo";

    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const validateStep2 = () => {
    const e = {};
    speakerFormFields.forEach((field) => {
      if (!field.is_required) return;
      const val = speakerFormValues[field.label];
      if (field.field_type === "Checkbox") {
        if (!val) e[field.label] = `${field.label} is required`;
      } else if (
        val === undefined ||
        val === null ||
        val.toString().trim() === ""
      ) {
        e[field.label] = `${field.label} is required`;
      }
      if (field.field_type === "EmailInput" && val && !validateEmail(val)) {
        e[field.label] = "Please enter a valid email address";
      }
    });
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const validateStep3 = () => {
    const e = {};
    if (!formData.experience)
      e.experience = "Please select your speaking experience";
    if (
      formData.twitter &&
      !formData.twitter.toLowerCase().includes("twitter.com") &&
      !formData.twitter.toLowerCase().includes("x.com")
    ) {
      e.twitter =
        "Please enter a valid Twitter URL (e.g. https://twitter.com/username)";
    }
    if (
      formData.linkedin &&
      !formData.linkedin.toLowerCase().includes("linkedin.com")
    ) {
      e.linkedin =
        "Please enter a valid LinkedIn URL (e.g. https://linkedin.com/in/yourprofile)";
    }
    if (formData.github && !formData.github.toLowerCase().includes("github.com")) {
      e.github =
        "Please enter a valid GitHub URL (e.g. https://github.com/username)";
    }
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const validateStep4 = () => {
    const e = {};
    if (!formData.agreement1)
      e.agreement1 = "Please confirm the accuracy of your information";
    if (!formData.agreement3)
      e.agreement3 = "Please accept the speaker agreement";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleStep1Next = () => validateStep1() && goToStep(2);
  const handleStep2Next = () => validateStep2() && goToStep(3);
  const handleStep3Next = () => validateStep3() && goToStep(4);

  /* ---------- Submit ---------- */
  const handleSubmit = async () => {
    if (!validateStep4()) return;

    setIsLoading(true);
    try {
      const token = localStorage.getItem("token");
      const username = `${formData.firstName} ${formData.lastName}`;

      let profilePictureBlob = null;
      let profilePictureName = null;
      if (formData.photo?.data) {
        const photoRes = await fetch(formData.photo.data);
        profilePictureBlob = await photoRes.blob();
        profilePictureName = formData.photo.name;
      }

      const profileFormData = new FormData();
      profileFormData.append("eventId", eventId);
      profileFormData.append("firstname", formData.firstName);
      profileFormData.append("lastname", formData.lastName);
      profileFormData.append("email", formData.email);
      profileFormData.append("phonenumber", formData.phone);
      profileFormData.append("city", formData.city);
      profileFormData.append("jobtitle", formData.jobTitle);
      profileFormData.append("company", formData.company);
      profileFormData.append("biography", formData.bio);
      if (profilePictureBlob) {
        profileFormData.append(
          "profilePicture",
          profilePictureBlob,
          profilePictureName,
        );
      }

      const profileRes = await fetch(
        `${API_BASE_URL}/speaker_registration/speaker_profile`,
        {
          method: "POST",
          headers: { Authorization: `Bearer ${token}` },
          body: profileFormData,
        },
      );
      const profileData = await profileRes.json();
      if (!profileRes.ok) {
        throw new Error(
          profileData?.message ||
            profileData?.error ||
            "Profile submission failed",
        );
      }

      const registrationId =
        profileData?.profile?.registrationId ||
        profileData?.registrationId ||
        profileData?.registration_id ||
        profileData?.result?.registration_id ||
        null;

      const customFormData = {};
      Object.entries(speakerFormValues).forEach(([k, v]) => {
        customFormData[k] = v;
      });

      const customRes = await fetch(
        `${API_BASE_URL}/speaker_registration/custom_form`,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            eventId: Number(eventId),
            emailId: formData.email,
            username,
            ...(registrationId && { registrationId }),
            custom_form: customFormData,
          }),
        },
      );
      const customData = await customRes.json();
      if (!customRes.ok) {
        throw new Error(
          customData?.message ||
            customData?.error ||
            "Custom form submission failed",
        );
      }

      const additionalRes = await fetch(
        `${API_BASE_URL}/speaker_registration/additional_information`,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            eventId: Number(eventId),
            emailId: formData.email,
            username,
            ...(registrationId && { registrationId }),
            additionalInfo: {
              experience: formData.experience || "",
              previousTalks: formData.previousTalks || "",
              twiter: formData.twitter || "",
              linkedin: formData.linkedin || "",
              github: formData.github || "",
            },
          }),
        },
      );
      const additionalData = await additionalRes.json();
      if (!additionalRes.ok) {
        throw new Error(
          additionalData?.message ||
            additionalData?.error ||
            "Additional info submission failed",
        );
      }

      const newSubmissionId =
        registrationId ||
        profileData?.registration_id ||
        profileData?.result?.registration_id ||
        `EF-SPK-${Math.floor(1000 + Math.random() * 9000)}`;

      setSubmissionId(newSubmissionId);
      setSubmissionDate(
        new Date().toLocaleDateString("en-US", {
          year: "numeric",
          month: "long",
          day: "numeric",
          hour: "2-digit",
          minute: "2-digit",
        }),
      );
      setIsSubmitted(true);
      localStorage.removeItem("speakerApplication");
    } catch (err) {
      console.error(err);
      setErrors({ submit: err.message || "Submission failed. Please try again." });
    } finally {
      setIsLoading(false);
    }
  };

  /* ---------- Custom field renderer (Step 2) ---------- */
  const ErrorMsg = ({ name }) =>
    errors[name] ? <div className="error-message">{errors[name]}</div> : null;

  const renderSpeakerField = (field, index) => {
    const { label, field_type, options, is_required, place_holder } = field;
    const value =
      speakerFormValues[label] ?? (field_type === "Checkbox" ? false : "");
    const hasError = !!errors[label];
    const fieldId = `spk_field_${index}`;
    const onChange = (val) => updateSpeakerFormValue(label, val);
    const labelEl = (
      <label className="form-label" htmlFor={fieldId}>
        {label} {is_required && <span className="required">*</span>}
      </label>
    );

    const inputTypeMap = {
      TextInput: "text",
      EmailInput: "email",
      PhoneInput: "tel",
      NumberInput: "number",
      DatePicker: "date",
      TimePicker: "time",
    };

    switch (field_type) {
      case "TextInput":
      case "EmailInput":
      case "PhoneInput":
      case "NumberInput":
      case "DatePicker":
      case "TimePicker":
        return (
          <div className="form-group" key={index}>
            {labelEl}
            <input
              id={fieldId}
              type={inputTypeMap[field_type] || "text"}
              className={`form-input${hasError ? " error" : ""}`}
              placeholder={place_holder || `Enter ${label}`}
              value={value}
              onChange={(e) => onChange(e.target.value)}
            />
            <ErrorMsg name={label} />
          </div>
        );

      case "TextArea":
        return (
          <div className="form-group full-width" key={index}>
            {labelEl}
            <textarea
              id={fieldId}
              className={`form-textarea${hasError ? " error" : ""}`}
              placeholder={place_holder || `Enter ${label}`}
              value={value}
              onChange={(e) => onChange(e.target.value)}
            />
            <ErrorMsg name={label} />
          </div>
        );

      case "Dropdown":
        return (
          <div className="form-group" key={index}>
            {labelEl}
            <select
              id={fieldId}
              className={`form-select${hasError ? " error" : ""}`}
              value={value}
              onChange={(e) => onChange(e.target.value)}
            >
              <option value="">Select {label}</option>
              {(options || []).map((opt, i) => (
                <option key={i} value={typeof opt === "object" ? opt.value : opt}>
                  {typeof opt === "object" ? opt.label : opt}
                </option>
              ))}
            </select>
            <ErrorMsg name={label} />
          </div>
        );

      case "RadioGroup":
        return (
          <div className="form-group" key={index}>
            <label className="form-label">
              {label} {is_required && <span className="required">*</span>}
            </label>
            <div className="radio-group">
              {(options || []).map((opt, i) => {
                const optVal = typeof opt === "object" ? opt.value : opt;
                const optLabel = typeof opt === "object" ? opt.label : opt;
                return (
                  <label key={i} className="radio-label">
                    <input
                      type="radio"
                      name={fieldId}
                      value={optVal}
                      checked={value === optVal}
                      onChange={(e) => onChange(e.target.value)}
                    />
                    {optLabel}
                  </label>
                );
              })}
            </div>
            <ErrorMsg name={label} />
          </div>
        );

      case "Checkbox":
        return (
          <div className="form-group full-width" key={index}>
            <div className="checkbox-group">
              <input
                type="checkbox"
                id={fieldId}
                checked={!!value}
                onChange={(e) => onChange(e.target.checked)}
              />
              <label className="checkbox-label" htmlFor={fieldId}>
                {label} {is_required && <span className="required">*</span>}
              </label>
            </div>
            <ErrorMsg name={label} />
          </div>
        );

      case "FileUpload":
        return (
          <div className="form-group full-width" key={index}>
            <label className="form-label">
              {label} {is_required && <span className="required">*</span>}
            </label>
            <FileUpload
              accept="*/*"
              maxSize={20 * 1024 * 1024}
              allowedTypes={[]}
              onFileSelect={(file) => onChange(file)}
              existingFile={value}
              isImage={false}
            />
            <ErrorMsg name={label} />
          </div>
        );

      default:
        return null;
    }
  };

  /* ---------- Download copy ---------- */
  const downloadApplication = () => {
    const applicationData = `
SPEAKER APPLICATION
===================
Application ID: ${submissionId}
Submitted: ${submissionDate}

SPEAKER PROFILE
---------------
Name: ${formData.firstName} ${formData.lastName}
Email: ${formData.email}
Phone: ${formData.phone}
City: ${formData.city}
Job Title: ${formData.jobTitle}
Company: ${formData.company}
Biography: ${formData.bio}

TALK DETAILS
------------
${Object.entries(speakerFormValues)
  .map(([k, v]) => `${k}: ${v}`)
  .join("\n")}

ADDITIONAL INFORMATION
----------------------
Experience: ${formData.experience}
Previous Talks: ${formData.previousTalks}
Twitter: ${formData.twitter}
LinkedIn: ${formData.linkedin}
GitHub: ${formData.github}
    `.trim();

    const blob = new Blob([applicationData], { type: "text/plain" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `Speaker-Application-${submissionId}.txt`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    alert("Application downloaded successfully!");
  };

  /* ---------- Shared pieces ---------- */
  const BackButton = () => (
    <button className="go-back" onClick={() => navigate(-1)}>
      <i className="fa-solid fa-arrow-left"></i> Back
    </button>
  );

  const FieldError = ({ name }) =>
    errors[name] ? <div className="error-message">{errors[name]}</div> : null;

  /* ---------- Success screen ---------- */
  if (isSubmitted) {
    return (
      <div className="speaker-form-page">
        <BackButton />
        <div className="container">
          <div className="form-container">
            <div className="success-section">
              <div className="success-icon">
                <i className="fas fa-check"></i>
              </div>
              <h2 className="success-title">Application Submitted!</h2>
              <p className="success-subtitle">
                Thank you for applying to speak at{" "}
                {eventData?.name || "the event"}. Your application has been
                received and will be reviewed by our committee.
              </p>

              <div className="success-details">
                <div className="detail-item">
                  <span className="detail-label">Application ID:</span>
                  <span className="detail-value">
                    <div className="submission-id">{submissionId}</div>
                  </span>
                </div>
                <div className="detail-item">
                  <span className="detail-label">Submitted On:</span>
                  <span className="detail-value">{submissionDate}</span>
                </div>
                <div className="detail-item">
                  <span className="detail-label">Review Timeline:</span>
                  <span className="detail-value">2-4 weeks</span>
                </div>
                <div className="detail-item">
                  <span className="detail-label">Notification:</span>
                  <span className="detail-value">
                    By{" "}
                    {eventData?.start_date
                      ? new Date(eventData.start_date).toLocaleDateString(
                          "en-US",
                          { year: "numeric", month: "long", day: "numeric" },
                        )
                      : "-"}
                  </span>
                </div>
              </div>

              <p className="success-note">
                <i className="fas fa-info-circle"></i>
                You will receive a confirmation email shortly. If you have any
                questions, please contact Organizer.
              </p>

              <div className="success-actions">
                <button
                  className="btn btn-secondary"
                  onClick={downloadApplication}
                >
                  <i className="fas fa-download"></i> Download Copy
                </button>
                <button
                  className="btn btn-primary"
                  onClick={() => navigate(`/eventpage/${eventId}#info`)}
                >
                  <i className="fas fa-calendar-alt"></i> View Conference
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  /* ---------- Loading screen ---------- */
  if (eventLoading) {
    return (
      <div className="speaker-form-page">
        <div className="container">
          <div className="form-container page-loading">
            <div className="spinner" />
            <div className="loading-text">Loading event details...</div>
          </div>
        </div>
      </div>
    );
  }
  /* ---------- Event loading error ---------- */
if (eventError) {
  return (
    <div className="speaker-closed-page">
      <div className="speaker-closed-content">
        <i className="fas fa-circle-exclamation speaker-closed-icon"></i>

        <h1>Event Not Available</h1>

        <p>We couldn't load this event. Please try again later.</p>

        <button
          className="speaker-closed-primary"
          onClick={() => navigate(`/eventpage/${eventId}#info`)}
        >
          Back to Event Details
        </button>
      </div>
    </div>
  );
}

/* ---------- Registration closed screen ---------- */
const registrationOpen = isSpeakerRegistrationOpen(
  eventData?.speaker_start_date,
  eventData?.speaker_end_date
);

if (!registrationOpen) {
  return (
    <div className="speaker-closed-page">
      <div className="speaker-closed-content">

        <div className="speaker-closed-icon">
          <i className="fas fa-calendar-xmark"></i>
        </div>

        <span className="speaker-closed-badge">
          <i className="fas fa-lock"></i>
          Speaker Registration Closed
        </span>

        <h1>Speaker Registration is Closed</h1>

        <p className="speaker-closed-subtitle">
          Thank you for your interest in speaking at{" "}
          <strong>{eventData?.name}</strong>.
        </p>

        <p className="speaker-closed-description">
          The submission deadline has passed or speaker registration
          is currently unavailable for this event.
        </p>

        <div className="speaker-closed-actions">
          <button
            className="speaker-closed-primary"
            onClick={() => navigate(`/eventpage/${eventId}#info`)}
          >
            <i className="fas fa-calendar-alt"></i>
            Back to Event Details
          </button>

          <button
            className="speaker-closed-secondary"
            onClick={() => navigate("/")}
          >
            <i className="fas fa-home"></i>
            Browse More Events
          </button>
        </div>

      </div>
    </div>
  );
}

  const bioWordCount = getWordCount(formData.bio);
  const bioWordCountClass =
    bioWordCount > 0 && bioWordCount < 10
      ? "over-limit"
      : bioWordCount > 300
        ? "over-limit"
        : bioWordCount > 250
          ? "near-limit"
          : "";

  const stepDefinitions = [
    { number: 1, label: "Speaker Profile", sub: "Tell us about yourself" },
    { number: 2, label: "Talk Details", sub: "Share your session details" },
    { number: 3, label: "Additional Info", sub: "Provide more information" },
    { number: 4, label: "Review & Submit", sub: "Check and submit" },
  ];

  const bannerStyle = eventData?.banner
    ? { backgroundImage: `url(${eventData.banner})` }
    : undefined;

  /* ---------- Main render ---------- */
  return (
    <div className="speaker-form-page">
      <BackButton />

      {toast && (
        <div className="toast">
          <i className="fas fa-check-circle"></i> {toast}
        </div>
      )}

      <div className="container">

        {/* ================= HERO BANNER ================= */}
        <div className="event-banner" style={bannerStyle}>
          <div className="event-banner-overlay"></div>
          <div className="event-banner-content">
            <div className="speaker-badge">
              <i className="fas fa-star"></i> CALL FOR SPEAKERS
            </div>

            <div className="event-title-row">
              {eventData?.image && (
                <div className="event-logo-wrapper">
                  <img
                    src={eventData.image}
                    alt={eventData?.name || "Event Logo"}
                    className="event-logo"
                  />
                </div>
              )}
              <h1 className="event-title">{eventData?.name}</h1>
            </div>

            <p className="event-subtitle">{eventData?.description}</p>

            <div className="event-meta">
              {eventData?.speaker_end_date && (
                <div className="meta-item">
                  <div className="meta-icon">
                    <i className="far fa-calendar-check"></i>
                  </div>
                  <div>
                    <div className="meta-label">Submission Deadline</div>
                   <div className="meta-value">
  {eventData.speaker_end_date
    ? new Date(eventData.speaker_end_date).toLocaleDateString("en-GB", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
        
      })
    : ""}
</div>
                  </div>
                </div>
              )}
              {(eventData?.city || eventData?.venue) && (
                <div className="meta-item">
                  <div className="meta-icon">
                    <i className="fas fa-location-dot"></i>
                  </div>
                  <div>
                    <div className="meta-label">Conference Venue</div>
                    <div className="meta-value">
                      {[eventData?.venue, eventData?.city]
                        .filter(Boolean)
                        .join(", ")}
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* ================= STEPPER ================= */}
        <div className="progress-container">
          <div className="progress-steps">
            {stepDefinitions.map((step, i) => {
              const isActive = currentStep === step.number;
              const isDone = currentStep > step.number;
              return (
                <React.Fragment key={step.number}>
                  <div className={`step ${isActive ? "active" : ""}`}>
                    <div
                      className={`step-circle ${isActive ? "active" : ""} ${
                        isDone ? "completed" : ""
                      }`}
                    >
                      {isDone ? <i className="fas fa-check"></i> : step.number}
                    </div>
                    <div className="step-text">
                      <div className={`step-label ${isActive ? "active" : ""}`}>
                        {step.label}
                      </div>
                      <div className="step-sub">{step.sub}</div>
                    </div>
                  </div>
                  {i < stepDefinitions.length - 1 && (
                    <div
                      className={`step-connector ${isDone ? "done" : ""}`}
                    ></div>
                  )}
                </React.Fragment>
              );
            })}
          </div>
        </div>

        {/* ================= FORM CARD ================= */}
        <div className="form-container">
          {isLoading && (
            <div className="loading-overlay">
              <div className="spinner"></div>
              <div className="loading-text">Submitting your application...</div>
            </div>
          )}

          <div className="form-sections">
            {/* ---------- STEP 1 ---------- */}
            {currentStep === 1 && (
              <div className="form-section active">
                <div className="section-header">
                  <div className="section-icon">
                    <i className="fas fa-user"></i>
                  </div>
                  <div>
                    <h2 className="section-title">Speaker Profile</h2>
                    <p className="section-subtitle">
                      Tell us about yourself. This information will be used in
                      conference materials if your talk is accepted.
                    </p>
                  </div>
                </div>

                <div className="step1-layout">
                  <form
                    className="form-grid"
                    onSubmit={(e) => e.preventDefault()}
                  >
                    <div className="form-group">
                      <label className="form-label" htmlFor="firstName">
                        First Name <span className="required">*</span>
                      </label>
                      <IconInput icon="far fa-user">
                        <input
                          type="text"
                          id="firstName"
                          className={`form-input ${errors.firstName ? "error" : ""}`}
                          placeholder="Enter your first name"
                          value={formData.firstName}
                          onChange={(e) =>
                            updateFormData("firstName", e.target.value)
                          }
                        />
                      </IconInput>
                      <FieldError name="firstName" />
                    </div>

                    <div className="form-group">
                      <label className="form-label" htmlFor="lastName">
                        Last Name <span className="required">*</span>
                      </label>
                      <IconInput icon="far fa-user">
                        <input
                          type="text"
                          id="lastName"
                          className={`form-input ${errors.lastName ? "error" : ""}`}
                          placeholder="Enter your last name"
                          value={formData.lastName}
                          onChange={(e) =>
                            updateFormData("lastName", e.target.value)
                          }
                        />
                      </IconInput>
                      <FieldError name="lastName" />
                    </div>

                    <div className="form-group">
                      <label className="form-label" htmlFor="email">
                        Professional Email <span className="required">*</span>
                      </label>
                      <IconInput icon="far fa-envelope">
                        <input
                          type="email"
                          id="email"
                          className={`form-input ${errors.email ? "error" : ""}`}
                          placeholder="your.email@company.com"
                          value={formData.email}
                          onChange={(e) =>
                            updateFormData("email", e.target.value)
                          }
                        />
                      </IconInput>
                      <FieldError name="email" />
                    </div>

                    <div className="form-group">
                      <label className="form-label" htmlFor="phone">
                        Phone Number <span className="required">*</span>
                      </label>
                      <div className="phone-input-wrapper">
                        <IconInput icon="fas fa-phone" className="country-wrap">
                          <select
                            className="country-code-select"
                            value={formData.countryCode || "+91"}
                            onChange={(e) =>
                              updateFormData("countryCode", e.target.value)
                            }
                          >
                            <option value="+91">🇮🇳 +91</option>
                            <option value="+1">🇺🇸 +1</option>
                            <option value="+44">🇬🇧 +44</option>
                            <option value="+61">🇦🇺 +61</option>
                            <option value="+971">🇦🇪 +971</option>
                            <option value="+65">🇸🇬 +65</option>
                            <option value="+81">🇯🇵 +81</option>
                            <option value="+49">🇩🇪 +49</option>
                            <option value="+33">🇫🇷 +33</option>
                            <option value="+86">🇨🇳 +86</option>
                          </select>
                        </IconInput>
                        <input
                          type="tel"
                          id="phone"
                          className={`form-input phone-number-input ${
                            errors.phone ? "error" : ""
                          }`}
                          placeholder="12345 67890"
                          value={formData.phone || ""}
                          maxLength={10}
                          onChange={(e) =>
                            updateFormData(
                              "phone",
                              e.target.value.replace(/\D/g, "").slice(0, 10),
                            )
                          }
                        />
                      </div>
                      <FieldError name="phone" />
                    </div>

                    <div className="form-group">
                      <label className="form-label" htmlFor="city">
                        City <span className="required">*</span>
                      </label>
                      <IconInput icon="fas fa-location-dot">
                        <select
                          id="city"
                          className={`form-select ${errors.city ? "error" : ""}`}
                          value={formData.city}
                          onChange={(e) =>
                            updateFormData("city", e.target.value)
                          }
                        >
                          <option value="">Select your city</option>
                          {citiesList.map((c) => (
                            <option key={c.id} value={c.city_name}>
                              {c.city_name}
                            </option>
                          ))}
                        </select>
                      </IconInput>
                      <FieldError name="city" />
                    </div>

                    <div className="form-group">
                      <label className="form-label" htmlFor="jobTitle">
                        Current Position / Job Title{" "}
                        <span className="required">*</span>
                      </label>
                      <IconInput icon="fas fa-briefcase">
                        <input
                          type="text"
                          id="jobTitle"
                          className={`form-input ${errors.jobTitle ? "error" : ""}`}
                          placeholder="e.g. Senior Database Architect, CTO, Developer Advocate"
                          value={formData.jobTitle}
                          onChange={(e) =>
                            updateFormData("jobTitle", e.target.value)
                          }
                        />
                      </IconInput>
                      <FieldError name="jobTitle" />
                    </div>

                    <div className="form-group">
                      <label className="form-label" htmlFor="company">
                        Company / Organization{" "}
                        <span className="required">*</span>
                      </label>
                      <IconInput icon="far fa-building">
                        <input
                          type="text"
                          id="company"
                          className={`form-input ${errors.company ? "error" : ""}`}
                          placeholder="Your company or organization name"
                          value={formData.company}
                          onChange={(e) =>
                            updateFormData("company", e.target.value)
                          }
                        />
                      </IconInput>
                      <FieldError name="company" />
                    </div>

                    <div className="form-group">
                      <div className="label-row">
                        <label className="form-label" htmlFor="bio">
                          Professional Biography{" "}
                          <span className="required">*</span>
                        </label>
                        <span className={`character-count ${bioWordCountClass}`}>
                          {bioWordCount}/300 words
                        </span>
                      </div>
                      <IconInput icon="far fa-file-lines" className="textarea-wrap">
                        <textarea
                          id="bio"
                          className={`form-textarea ${errors.bio ? "error" : ""}`}
                          placeholder="Tell us about your professional background, expertise, and achievements..."
                          value={formData.bio}
                          onChange={(e) =>
                            updateFormData("bio", e.target.value)
                          }
                        />
                      </IconInput>
                      <FieldError name="bio" />
                    </div>
                  </form>

                  {/* ---- Profile photo side card ---- */}
                  <aside className="photo-card">
                    <div className="photo-card-head">
                      <div className="photo-card-icon">
                        <i className="fas fa-camera"></i>
                      </div>
                      <div>
                        <div className="photo-card-title">
                          Profile Photo <span className="required">*</span>
                        </div>
                        <div className="photo-card-hint">
                          High-quality headshot (minimum 400×400px, max 5MB)
                        </div>
                      </div>
                    </div>

                    <FileUpload
                      variant="avatar"
                      accept="image/*"
                      maxSize={5 * 1024 * 1024}
                      allowedTypes={["image/jpeg", "image/png", "image/jpg"]}
                      onFileSelect={(file) => updateFormData("photo", file)}
                      existingFile={formData.photo}
                      isImage={true}
                    />
                    <FieldError name="photo" />
                  </aside>
                </div>

                <div className="form-actions">
                  <div></div>
                  <div className="form-actions-right">
            
                    <button
                      type="button"
                      className="btn btn-primary"
                      onClick={handleStep1Next}
                    >
                      Next: Talk Details <i className="fas fa-arrow-right"></i>
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* ---------- STEP 2 ---------- */}
            {currentStep === 2 && (
              <div className="form-section active">
                <div className="section-header">
                  <div className="section-icon">
                    <i className="fa-solid fa-message"></i>
                  </div>
                  <div>
                    <h2 className="section-title">Talk Details</h2>
                    <p className="section-subtitle">
                      Tell us about your proposed talk. Be specific about what
                      attendees will learn.
                    </p>
                  </div>
                </div>

                <div className="requirements-list">
                  <h4>
                    <i className="fas fa-lightbulb"></i> Submission Requirements
                  </h4>
                  <ul>
                    <li>
                      Talk topic should be clear and descriptive (max 100
                      characters)
                    </li>
                    <li>
                      Abstract should be 10-400 words explaining what attendees
                      will learn
                    </li>
                  </ul>
                </div>

                <form
                  className="form-grid"
                  onSubmit={(e) => e.preventDefault()}
                >
                  {speakerFormFields.map((field, index) =>
                    renderSpeakerField(field, index),
                  )}
                </form>

                {errors.submit && (
                  <div className="error-message submit-error">
                    {errors.submit}
                  </div>
                )}

                <div className="form-actions">
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={() => goToStep(1)}
                  >
                    <i className="fas fa-arrow-left"></i> Back
                  </button>
                  <div className="form-actions-right">
                  
                    <button
                      type="button"
                      className="btn btn-primary"
                      onClick={handleStep2Next}
                    >
                      Next: Additional Info{" "}
                      <i className="fas fa-arrow-right"></i>
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* ---------- STEP 3 ---------- */}
            {currentStep === 3 && (
              <div className="form-section active">
                <div className="section-header">
                  <div className="section-icon">
                    <i className="fas fa-info-circle"></i>
                  </div>
                  <div>
                    <h2 className="section-title">Additional Information</h2>
                    <p className="section-subtitle">
                      Help us understand your experience and requirements.
                    </p>
                  </div>
                </div>

                <form
                  className="form-grid"
                  onSubmit={(e) => e.preventDefault()}
                >
                  <div className="form-group">
                    <label className="form-label" htmlFor="experience">
                      Speaking Experience <span className="required">*</span>
                    </label>
                    <select
                      id="experience"
                      className={`form-select ${errors.experience ? "error" : ""}`}
                      value={formData.experience}
                      onChange={(e) =>
                        updateFormData("experience", e.target.value)
                      }
                    >
                      <option value="">Select experience level</option>
                      <option value="first-time">First-time speaker</option>
                      <option value="local">Local meetups/small events</option>
                      <option value="national">National conferences</option>
                      <option value="international">
                        International conferences
                      </option>
                      <option value="keynote">Keynote speaker</option>
                    </select>
                    <FieldError name="experience" />
                  </div>

                  <div className="form-group">
                    <label className="form-label" htmlFor="previousTalks">
                      Previous Talks (Optional)
                    </label>
                    <textarea
                      id="previousTalks"
                      className="form-textarea textarea-sm"
                      placeholder="List notable previous speaking engagements, if any"
                      value={formData.previousTalks}
                      onChange={(e) =>
                        updateFormData("previousTalks", e.target.value)
                      }
                    />
                  </div>

                  <div className="form-group full-width">
                    <label className="form-label">Relevant Links</label>

                    <div className="links-grid">
                      <div>
                        <label className="form-label sub" htmlFor="twitter">
                          Twitter
                        </label>
                        <IconInput icon="fab fa-twitter">
                          <input
                            type="url"
                            id="twitter"
                            className={`form-input ${errors.twitter ? "error" : ""}`}
                            placeholder="https://twitter.com/username"
                            value={formData.twitter}
                            onChange={(e) =>
                              updateFormData("twitter", e.target.value)
                            }
                          />
                        </IconInput>
                        <FieldError name="twitter" />
                      </div>

                      <div>
                        <label className="form-label sub" htmlFor="linkedin">
                          LinkedIn
                        </label>
                        <IconInput icon="fab fa-linkedin-in">
                          <input
                            type="url"
                            id="linkedin"
                            className={`form-input ${errors.linkedin ? "error" : ""}`}
                            placeholder="https://linkedin.com/in/yourprofile"
                            value={formData.linkedin}
                            onChange={(e) =>
                              updateFormData("linkedin", e.target.value)
                            }
                          />
                        </IconInput>
                        <FieldError name="linkedin" />
                      </div>

                      <div>
                        <label className="form-label sub" htmlFor="github">
                          GitHub
                        </label>
                        <IconInput icon="fab fa-github">
                          <input
                            type="url"
                            id="github"
                            className={`form-input ${errors.github ? "error" : ""}`}
                            placeholder="https://github.com/username"
                            value={formData.github}
                            onChange={(e) =>
                              updateFormData("github", e.target.value)
                            }
                          />
                        </IconInput>
                        <FieldError name="github" />
                      </div>
                    </div>
                  </div>

                  {formData.travelSupport && (
                    <div className="form-group full-width">
                      <label className="form-label" htmlFor="travelDetails">
                        Travel Support Details
                      </label>
                      <textarea
                        id="travelDetails"
                        className="form-textarea textarea-sm"
                        placeholder="Please provide details about your travel requirements"
                        value={formData.travelDetails}
                        onChange={(e) =>
                          updateFormData("travelDetails", e.target.value)
                        }
                      />
                    </div>
                  )}
                </form>

                <div className="form-actions">
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={() => goToStep(2)}
                  >
                    <i className="fas fa-arrow-left"></i> Back
                  </button>
                  <div className="form-actions-right">
                    
                    <button
                      type="button"
                      className="btn btn-primary"
                      onClick={handleStep3Next}
                    >
                      Next: Review & Submit{" "}
                      <i className="fas fa-arrow-right"></i>
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* ---------- STEP 4 ---------- */}
            {currentStep === 4 && (
              <div className="form-section active">
                <div className="section-header">
                  <div className="section-icon">
                    <i className="fas fa-check-circle"></i>
                  </div>
                  <div>
                    <h2 className="section-title">Review & Submit</h2>
                    <p className="section-subtitle">
                      Please review your information before submitting your
                      application.
                    </p>
                  </div>
                </div>

                <div className="success-details review-box">
                  <h4>Speaker Profile</h4>
                  <div className="success-pro-wrap">
                    <div className="detail-item">
                      <span className="detail-label">Speaker Name:</span>
                      <span className="detail-value">
                        {formData.firstName} {formData.lastName}
                      </span>
                    </div>
                    <div className="detail-item">
                      <span className="detail-label">Professional Email:</span>
                      <span className="detail-value">{formData.email}</span>
                    </div>
                    <div className="detail-item">
                      <span className="detail-label">Phone Number:</span>
                      <span className="detail-value">
                        {formData.countryCode} {formData.phone}
                      </span>
                    </div>
                    <div className="detail-item">
                      <span className="detail-label">City:</span>
                      <span className="detail-value">{formData.city}</span>
                    </div>
                    <div className="detail-item">
                      <span className="detail-label">
                        Current Position/Job Title:
                      </span>
                      <span className="detail-value">{formData.jobTitle}</span>
                    </div>
                    <div className="detail-item">
                      <span className="detail-label">
                        Company/Organization:
                      </span>
                      <span className="detail-value">{formData.company}</span>
                    </div>
                    <div className="detail-item">
                      <span className="detail-label">
                        Professional Biography:
                      </span>
                      <span className="detail-value">{formData.bio}</span>
                    </div>
                  </div>

                  <h4>Talk Details</h4>
                  <div className="success-pro-wrap">
                    {Object.entries(speakerFormValues).map(([key, value]) =>
                      value ? (
                        <div className="detail-item" key={key}>
                          <span className="detail-label">{key}:</span>
                          <span className="detail-value">
                            {typeof value === "boolean"
                              ? value
                                ? "Yes"
                                : "No"
                              : typeof value === "object"
                                ? value.name
                                : value}
                          </span>
                        </div>
                      ) : null,
                    )}
                  </div>

                  <h4>Additional Information</h4>
                  <div className="success-pro-wrap">
                    <div className="detail-item">
                      <span className="detail-label">Experience:</span>
                      <span className="detail-value">
                        {formData.experience}
                      </span>
                    </div>
                    <div className="detail-item">
                      <span className="detail-label">
                        Previous Talks (Optional):
                      </span>
                      <span className="detail-value">
                        {formData.previousTalks}
                      </span>
                    </div>
                    {formData.twitter && (
                      <div className="detail-item">
                        <span className="detail-label">Twitter:</span>
                        <span className="detail-value">{formData.twitter}</span>
                      </div>
                    )}
                    {formData.linkedin && (
                      <div className="detail-item">
                        <span className="detail-label">LinkedIn:</span>
                        <span className="detail-value">
                          {formData.linkedin}
                        </span>
                      </div>
                    )}
                    {formData.github && (
                      <div className="detail-item">
                        <span className="detail-label">GitHub:</span>
                        <span className="detail-value">{formData.github}</span>
                      </div>
                    )}
                  </div>
                </div>

                <div className="form-group full-width">
                  <div className="checkbox-group">
                    <input
                      type="checkbox"
                      id="agreement1"
                      checked={formData.agreement1}
                      onChange={(e) =>
                        updateFormData("agreement1", e.target.checked)
                      }
                    />
                    <label className="checkbox-label" htmlFor="agreement1">
                      I confirm that all information provided is accurate and
                      complete
                    </label>
                  </div>
                  <FieldError name="agreement1" />
                </div>

                <div className="form-group full-width">
                  <div className="checkbox-group">
                    <input
                      type="checkbox"
                      id="agreement3"
                      checked={formData.agreement3}
                      onChange={(e) =>
                        updateFormData("agreement3", e.target.checked)
                      }
                    />
                    <label className="checkbox-label" htmlFor="agreement3">
                      I acknowledge and agree to the{" "}
                      <a
                        href={`https://eventfork.com/eventpage/${eventId}#info`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="guideline-link"
                      >
                        speaker guidelines
                      </a>{" "}
                      for this event.
                    </label>
                  </div>
                  <FieldError name="agreement3" />
                </div>

                {errors.submit && (
                  <div className="error-message submit-error">
                    {errors.submit}
                  </div>
                )}

                <div className="form-actions">
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={() => goToStep(3)}
                  >
                    <i className="fas fa-arrow-left"></i> Back
                  </button>
                  <div className="form-actions-right">
                    
                    <button
                      type="button"
                      className="btn btn-primary"
                      onClick={handleSubmit}
                      disabled={!formData.agreement1 || !formData.agreement3}
                    >
                      <i className="fas fa-paper-plane"></i> Submit Application
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default SpeakerForm;